import { useState, useEffect } from 'react'
import { Plus, Search, Pencil, Trash2, Paperclip, PenTool, Download, Phone, Calendar, X, UserCog, UserPlus, Link2, Unlink, Mail } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { AllegatiModal } from '../../../components/allegati/AllegatiModal'
import { FirmaUploadModal } from '../../../components/personale/FirmaUploadModal'
import { BulkDownloadModal } from '../../../components/personale/BulkDownloadModal'
import { Personale, PersonaleForm, StatoPersonale, RUOLI_PERSONALE } from '../../../types/personale'

const emptyForm: PersonaleForm = {
  nome: '',
  cognome: '',
  data_nascita: '',
  indirizzo_residenza: '',
  telefono: '',
  data_assunzione: '',
  tipo_contratto: '',
  ruolo: '',
  reparto: '',
  data_fine_rapporto: '',
  stato: 'attivo',
  skill: [],
  note: ''
}

const statoConfig: Record<StatoPersonale, { label: string; color: string }> = {
  attivo: { label: 'Attivo', color: 'bg-green-100 text-green-700' },
  temporaneamente_non_disponibile: { label: 'Non Disponibile', color: 'bg-yellow-100 text-yellow-700' },
  cessato: { label: 'Cessato', color: 'bg-red-100 text-red-700' },
}

export default function PersonalePage() {
  const [personale, setPersonale] = useState<Personale[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStato, setFilterStato] = useState<StatoPersonale | ''>('')
  const [filterReparto, setFilterReparto] = useState('')

  // CRUD
  const [showModal, setShowModal] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Personale | null>(null)
  const [form, setForm] = useState<PersonaleForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Personale | null>(null)

  // Skill input
  const [skillInput, setSkillInput] = useState('')

  // Allegati
  const [showAllegati, setShowAllegati] = useState(false)
  const [allegatiPersonaId, setAllegatiPersonaId] = useState('')

  // Firma
  const [showFirma, setShowFirma] = useState(false)
  const [firmaPersona, setFirmaPersona] = useState<Personale | null>(null)

  // Collegamento utente
  const [showUserModal, setShowUserModal] = useState(false)
  const [userModalPersona, setUserModalPersona] = useState<Personale | null>(null)
  const [utentiDisponibili, setUtentiDisponibili] = useState<{ id: string; email: string; username: string }[]>([])
  const [loadingUtenti, setLoadingUtenti] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [creatingUser, setCreatingUser] = useState(false)

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showBulkDownload, setShowBulkDownload] = useState(false)

  useEffect(() => {
    loadPersonale()
  }, [])

  const loadPersonale = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/personale')
      setPersonale(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Reparti unici per il filtro
  const reparti = [...new Set(personale.map(p => p.reparto).filter(Boolean) as string[])].sort()

  // Filtro
  const filteredPersonale = personale.filter(p => {
    const s = search.toLowerCase()
    const matchSearch = !search ||
      p.nome.toLowerCase().includes(s) ||
      p.cognome.toLowerCase().includes(s) ||
      (p.ruolo?.toLowerCase() || '').includes(s) ||
      (p.telefono || '').includes(search)
    const matchStato = !filterStato || p.stato === filterStato
    const matchReparto = !filterReparto || p.reparto === filterReparto
    return matchSearch && matchStato && matchReparto
  })

  // CRUD handlers
  const handleCreate = () => {
    setEditingPersona(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (persona: Personale) => {
    setEditingPersona(persona)
    setForm({
      nome: persona.nome,
      cognome: persona.cognome,
      data_nascita: persona.data_nascita || '',
      indirizzo_residenza: persona.indirizzo_residenza || '',
      telefono: persona.telefono || '',
      data_assunzione: persona.data_assunzione || '',
      tipo_contratto: persona.tipo_contratto || '',
      ruolo: persona.ruolo || '',
      reparto: persona.reparto || '',
      data_fine_rapporto: persona.data_fine_rapporto || '',
      stato: persona.stato,
      skill: persona.skill || [],
      note: persona.note || ''
    })
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data: Record<string, any> = {
        nome: form.nome,
        cognome: form.cognome,
        stato: form.stato,
      }

      // Campi opzionali - invia solo se compilati
      if (form.data_nascita) data.data_nascita = form.data_nascita
      if (form.indirizzo_residenza) data.indirizzo_residenza = form.indirizzo_residenza
      if (form.telefono) data.telefono = form.telefono
      if (form.data_assunzione) data.data_assunzione = form.data_assunzione
      if (form.tipo_contratto) data.tipo_contratto = form.tipo_contratto
      if (form.ruolo) data.ruolo = form.ruolo
      if (form.reparto) data.reparto = form.reparto
      if (form.data_fine_rapporto) data.data_fine_rapporto = form.data_fine_rapporto
      if (form.skill.length > 0) data.skill = form.skill
      if (form.note) data.note = form.note

      // In edit mode, invia anche i campi vuoti per permettere la cancellazione
      if (editingPersona) {
        data.data_nascita = form.data_nascita || null
        data.indirizzo_residenza = form.indirizzo_residenza || null
        data.telefono = form.telefono || null
        data.data_assunzione = form.data_assunzione || null
        data.tipo_contratto = form.tipo_contratto || null
        data.ruolo = form.ruolo || null
        data.reparto = form.reparto || null
        data.data_fine_rapporto = form.data_fine_rapporto || null
        data.skill = form.skill.length > 0 ? form.skill : null
        data.note = form.note || null
      }

      if (editingPersona) {
        await api.put(`/v1/personale/${editingPersona.id}`, data)
        setToast({ message: 'Persona aggiornata', type: 'success' })
      } else {
        await api.post('/v1/personale', data)
        setToast({ message: 'Persona creata', type: 'success' })
      }

      setShowModal(false)
      loadPersonale()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return
    try {
      await api.delete(`/v1/personale/${confirmDelete.id}`)
      setToast({ message: 'Persona eliminata', type: 'success' })
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(confirmDelete.id)
        return next
      })
      loadPersonale()
    } catch {
      setToast({ message: "Errore nell'eliminazione", type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  // Selezione multipla
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPersonale.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredPersonale.map(p => p.id)))
    }
  }

  // Collegamento utente handlers
  const openUserModal = async (persona: Personale) => {
    setUserModalPersona(persona)
    setSelectedUserId('')
    setShowUserModal(true)
    setLoadingUtenti(true)
    try {
      const res = await api.get('/v1/anagrafiche/utenti')
      // Filtra utenti non ancora collegati a nessuna persona (o collegati a questa)
      const usedUserIds = new Set(personale.filter(p => p.user_id && p.id !== persona.id).map(p => p.user_id))
      setUtentiDisponibili(
        res.data
          .filter((u: any) => u.is_active && !usedUserIds.has(u.id))
          .map((u: any) => ({ id: u.id, email: u.email, username: u.username }))
      )
    } catch {
      setUtentiDisponibili([])
    }
    setLoadingUtenti(false)
  }

  const handleCollegaUtente = async () => {
    if (!userModalPersona || !selectedUserId) return
    try {
      await api.post(`/v1/personale/${userModalPersona.id}/collega-utente`, { user_id: selectedUserId })
      setToast({ message: 'Utente collegato', type: 'success' })
      setShowUserModal(false)
      loadPersonale()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nel collegamento', type: 'error' })
    }
  }

  const handleCreaUtente = async () => {
    if (!userModalPersona) return
    setCreatingUser(true)
    try {
      const res = await api.post(`/v1/personale/${userModalPersona.id}/crea-utente`)
      setToast({ message: `Utente creato: ${res.data.user_email} (password: 1234)`, type: 'success' })
      setShowUserModal(false)
      loadPersonale()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nella creazione utente', type: 'error' })
    } finally {
      setCreatingUser(false)
    }
  }

  const handleScollegaUtente = async (persona: Personale) => {
    try {
      await api.delete(`/v1/personale/${persona.id}/scollega-utente`)
      setToast({ message: 'Utente scollegato', type: 'success' })
      loadPersonale()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore', type: 'error' })
    }
  }

  const getInitials = (nome: string, cognome: string) => {
    return (cognome.charAt(0) + nome.charAt(0)).toUpperCase()
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT')
  }

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Persona"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.cognome} ${confirmDelete?.nome}"? Verranno eliminati anche tutti i documenti associati.`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      <AllegatiModal
        isOpen={showAllegati}
        onClose={() => setShowAllegati(false)}
        entityType="personale"
        entityId={allegatiPersonaId}
      />

      {firmaPersona && (
        <FirmaUploadModal
          isOpen={showFirma}
          onClose={() => { setShowFirma(false); setFirmaPersona(null) }}
          personaleId={firmaPersona.id}
          nomeCognome={`${firmaPersona.cognome} ${firmaPersona.nome}`}
          hasFirma={firmaPersona.has_firma}
          onFirmaChanged={loadPersonale}
        />
      )}

      <BulkDownloadModal
        isOpen={showBulkDownload}
        onClose={() => setShowBulkDownload(false)}
        personaleIds={Array.from(selectedIds)}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personale</h1>
          <p className="text-gray-500">
            {filteredPersonale.length} {filteredPersonale.length === 1 ? 'persona' : 'persone'}
            {selectedIds.size > 0 && ` · ${selectedIds.size} selezionat${selectedIds.size === 1 ? 'a' : 'e'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <button
              onClick={() => setShowBulkDownload(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Scarica Documenti ({selectedIds.size})
            </button>
          )}
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuovo
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Ricerca */}
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, cognome, ruolo, telefono..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Stato */}
          <select
            value={filterStato}
            onChange={(e) => setFilterStato(e.target.value as StatoPersonale | '')}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="attivo">Attivo</option>
            <option value="temporaneamente_non_disponibile">Non Disponibile</option>
            <option value="cessato">Cessato</option>
          </select>

          {/* Reparto */}
          <select
            value={filterReparto}
            onChange={(e) => setFilterReparto(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti i reparti</option>
            {reparti.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Select all */}
        {filteredPersonale.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selectedIds.size === filteredPersonale.length && filteredPersonale.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              Seleziona tutti ({filteredPersonale.length})
            </label>
          </div>
        )}
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Caricamento...</div>
      ) : filteredPersonale.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <UserCog className="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p className="text-lg">{search || filterStato || filterReparto ? 'Nessun risultato' : 'Nessuna persona inserita'}</p>
          {!search && !filterStato && !filterReparto && (
            <p className="text-sm mt-1">Clicca "Nuovo" per aggiungere personale</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPersonale.map(persona => (
            <div
              key={persona.id}
              className={`bg-white rounded-xl shadow-sm border-2 transition-colors ${
                selectedIds.has(persona.id) ? 'border-primary-300 bg-primary-50/30' : 'border-transparent'
              }`}
            >
              <div className="p-5">
                {/* Top: Checkbox + Avatar + Status */}
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(persona.id)}
                    onChange={() => toggleSelection(persona.id)}
                    className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-lg flex-shrink-0">
                    {getInitials(persona.nome, persona.cognome)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {persona.cognome} {persona.nome}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      {[persona.ruolo, persona.reparto].filter(Boolean).join(' · ') || 'Nessun ruolo'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statoConfig[persona.stato].color}`}>
                    {statoConfig[persona.stato].label}
                  </span>
                </div>

                {/* Info */}
                <div className="space-y-1.5 mb-4 pl-7">
                  {persona.telefono && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      {persona.telefono}
                    </div>
                  )}
                  {persona.data_assunzione && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      Assunto il {formatDate(persona.data_assunzione)}
                    </div>
                  )}
                  {persona.tipo_contratto && (
                    <div className="text-xs text-gray-400 pl-5.5">
                      {persona.tipo_contratto}
                    </div>
                  )}
                  {persona.has_firma && (
                    <div className="flex items-center gap-2 text-xs text-green-600">
                      <PenTool className="w-3 h-3" />
                      Firma presente
                    </div>
                  )}
                  {persona.user_email ? (
                    <div className="flex items-center gap-2 text-xs text-blue-600">
                      <Mail className="w-3 h-3" />
                      {persona.user_email}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <UserCog className="w-3 h-3" />
                      Nessun utente collegato
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => { setAllegatiPersonaId(persona.id); setShowAllegati(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Documenti"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span className="hidden xl:inline">Doc</span>
                  </button>
                  <button
                    onClick={() => { setFirmaPersona(persona); setShowFirma(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Firma"
                  >
                    <PenTool className="w-4 h-4" />
                  </button>
                  {persona.user_id ? (
                    <button
                      onClick={() => handleScollegaUtente(persona)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Scollega utente"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => openUserModal(persona)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Collega o crea utente"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span className="hidden xl:inline">Utente</span>
                    </button>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={() => handleEdit(persona)}
                    className="p-2 text-gray-400 hover:text-primary-600 rounded-lg transition-colors"
                    title="Modifica"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(persona)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modale Crea/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingPersona ? 'Modifica Persona' : 'Nuova Persona'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Nome / Cognome */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    required
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    required
                    value={form.cognome}
                    onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Data Nascita / Telefono */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                  <input
                    type="date"
                    value={form.data_nascita}
                    onChange={(e) => setForm({ ...form, data_nascita: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    placeholder="es. +39 333 1234567"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Indirizzo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo Residenza</label>
                <input
                  type="text"
                  value={form.indirizzo_residenza}
                  onChange={(e) => setForm({ ...form, indirizzo_residenza: e.target.value })}
                  placeholder="Via, CAP, Citta'"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Data Assunzione / Tipo Contratto */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Assunzione</label>
                  <input
                    type="date"
                    value={form.data_assunzione}
                    onChange={(e) => setForm({ ...form, data_assunzione: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Contratto</label>
                  <input
                    type="text"
                    value={form.tipo_contratto}
                    onChange={(e) => setForm({ ...form, tipo_contratto: e.target.value })}
                    placeholder="es. Indeterminato, Determinato"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Ruolo / Reparto */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                  <select
                    value={form.ruolo}
                    onChange={(e) => setForm({ ...form, ruolo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    <option value="">Seleziona ruolo...</option>
                    {RUOLI_PERSONALE.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reparto</label>
                  <input
                    type="text"
                    value={form.reparto}
                    onChange={(e) => setForm({ ...form, reparto: e.target.value })}
                    placeholder="es. Produzione, Ufficio"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Skill */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Skill</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Competenze</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          const val = skillInput.trim()
                          if (val && !form.skill.includes(val)) {
                            setForm({ ...form, skill: [...form.skill, val] })
                          }
                          setSkillInput('')
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Digita una skill e premi Invio"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = skillInput.trim()
                        if (val && !form.skill.includes(val)) {
                          setForm({ ...form, skill: [...form.skill, val] })
                        }
                        setSkillInput('')
                      }}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                    >
                      Aggiungi
                    </button>
                  </div>
                  {form.skill.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.skill.map((s, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
                        >
                          {s}
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, skill: form.skill.filter((_, i) => i !== idx) })}
                            className="ml-0.5 text-primary-500 hover:text-primary-800"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Data Fine Rapporto / Stato */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fine Rapporto</label>
                  <input
                    type="date"
                    value={form.data_fine_rapporto}
                    onChange={(e) => setForm({ ...form, data_fine_rapporto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                  <select
                    value={form.stato}
                    onChange={(e) => setForm({ ...form, stato: e.target.value as StatoPersonale })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="attivo">Attivo</option>
                    <option value="temporaneamente_non_disponibile">Temporaneamente Non Disponibile</option>
                    <option value="cessato">Cessato</option>
                  </select>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale Collega/Crea Utente */}
      {showUserModal && userModalPersona && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Utente per {userModalPersona.cognome} {userModalPersona.nome}
              </h2>
              <button
                onClick={() => setShowUserModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Opzione 1: Crea nuovo utente */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Crea nuovo utente
                </h3>
                <p className="text-xs text-blue-600 mb-3">
                  Verrà creato un utente con email automatica e password <strong>1234</strong>.
                  Al primo login dovrà cambiarla.
                </p>
                <button
                  onClick={handleCreaUtente}
                  disabled={creatingUser}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {creatingUser ? 'Creazione in corso...' : 'Crea Utente'}
                </button>
              </div>

              {/* Divisore */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">oppure</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Opzione 2: Collega utente esistente */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Collega utente esistente
                </h3>
                {loadingUtenti ? (
                  <div className="text-sm text-gray-400">Caricamento utenti...</div>
                ) : utentiDisponibili.length === 0 ? (
                  <div className="text-sm text-gray-400">Nessun utente disponibile da collegare</div>
                ) : (
                  <>
                    <select
                      value={selectedUserId}
                      onChange={e => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm mb-3"
                    >
                      <option value="">Seleziona utente...</option>
                      {utentiDisponibili.map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                      ))}
                    </select>
                    <button
                      onClick={handleCollegaUtente}
                      disabled={!selectedUserId}
                      className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-30 text-sm font-medium"
                    >
                      Collega
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
